import React, { useState, useEffect } from 'react';
import {
  strategistService,
  type DailyBlueprintResponse,
  type CalendarOverviewResponse,
  type BriefingItem
} from '@/services/strategistService';
import { ExecutiveBriefingCard } from '@/components/Strategist/ExecutiveBriefingCard';
import { motivoLegivel } from '@/services/erroLegivel';
import { detalheLegivel } from '@/services/erros';
import { LightningIcon } from '@/components/Strategist/SvgIcons';

export const DailyBlueprintHub: React.FC = () => {
  // Vazio nao e "sem data": e "hoje, e quem decide que dia e hoje e o SERVIDOR".
  // Estes tres estados eram literais — '2026-08-20', 8, 2026 — numa tela cujo
  // titulo e "Ordem de Producao DIARIA". Ela abria sempre num dia fixo do
  // passado, e o calendario sempre em agosto de 2026, para todo mundo e para
  // sempre. Sem parametro, o backend responde pelo MASTER CLOCK (ADR-0015), e
  // o mes/ano do calendario passam a vir da resposta — uma fonte de verdade so.
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [blueprint, setBlueprint] = useState<DailyBlueprintResponse | null>(null);
  const [calendarOverview, setCalendarOverview] = useState<CalendarOverviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>('TODAS');

  const fetchBlueprint = async (dateStr: string) => {
    setLoading(true);
    setErro(null);
    try {
      const data = await strategistService.getDailyBlueprint(dateStr);
      setBlueprint(data);
      // Primeira carga: o servidor acabou de dizer que dia e hoje. Adotamos a
      // resposta dele como o dia selecionado e posicionamos o calendario nela.
      if (!dateStr && data.data_iso) {
        const [ano, mes] = data.data_iso.split('-').map(Number);
        setSelectedDate(data.data_iso);
        setSelectedYear(ano);
        setSelectedMonth(mes);
      }
    } catch (err) {
      // Sem este estado, a falha ficava INVISIVEL: `blueprint` continuava nulo
      // e o render caia no ramo `loading || !blueprint`, deixando a tela em
      // "Carregando pautas..." para sempre. Foi assim que as quatro telas do
      // Estrategista pareceram lentas por meses, quando na verdade estavam
      // recebendo 404 em toda chamada.
      setErro(motivoLegivel(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarOverview = async (ano: number, mes: number) => {
    try {
      const calData = await strategistService.getCalendarPlannerOverview(ano, mes);
      setCalendarOverview(calData);
    } catch (err) {
      console.error('Erro ao buscar calendário:', err);
    }
  };

  // Dois efeitos, e nao um: trocar o MES do calendario nao tem por que recarregar
  // as pautas do DIA, e vice-versa. Juntos, cada clique no calendario disparava
  // as duas chamadas.
  useEffect(() => {
    fetchBlueprint(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (selectedYear && selectedMonth) fetchCalendarOverview(selectedYear, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const handleToggleItem = async (itemId: string, currentStatus: boolean) => {
    try {
      await strategistService.toggleBlueprintItem(itemId, currentStatus);
      fetchBlueprint(selectedDate);
      showToast(currentStatus ? 'Pauta marcada como concluída.' : 'Pauta reaberta como pendente.');
    } catch {
      showToast('Erro ao atualizar status da pauta.');
    }
  };

  const handleDispatchItem = async (itemId: string) => {
    try {
      const res = await strategistService.dispatchBlueprintItem(itemId);
      if (res.success) {
        fetchBlueprint(selectedDate);
        // O texto vem do SERVIDOR: e ele quem sabe o que de fato aconteceu.
        // Aqui havia a frase fixa "despachada com sucesso para a esteira
        // autonoma" — que continuava mentindo na tela DEPOIS de o backend ter
        // passado a responder honesto (F-34): marcacao local, sem esteira.
        showToast(res.message || 'Pauta marcada como em produção neste painel.');
      }
    } catch {
      showToast('Erro ao despachar pauta.');
    }
  };

  const handleDispatchAll = async () => {
    if (!blueprint) return;
    const allItems: BriefingItem[] = [];
    Object.values(blueprint.horizontes).forEach(h => {
      if (h?.itens) allItems.push(...h.itens);
    });

    const pending = allItems.filter(i => !i.concluido);
    if (pending.length === 0) {
      showToast('Nenhuma pauta pendente neste dia — não havia o que marcar.');
      return;
    }

    // A frase daqui era FIXA ("Todas as N pautas pendentes foram despachadas
    // para IA!") e saia igual em todo cenario: rede caida, 401 no meio do lote,
    // zero chamadas bem-sucedidas. Pior, contradizia o proprio backend, que
    // desde o F-34 responde honesto — a marcacao e local, nao ha esteira. O
    // handler individual desta mesma tela ja fala pelo `res.message`; o lote
    // passa a contar o que de fato voltou e a repetir o literal do servidor.
    let ok = 0;
    let mensagemServidor = '';
    const falhas: string[] = [];
    for (const item of pending) {
      try {
        const res = await strategistService.dispatchBlueprintItem(item.id);
        if (res.success) {
          ok += 1;
          if (!mensagemServidor && res.message) mensagemServidor = res.message;
        } else {
          // 200 com `success: false` tambem e falha — e quem explica e o servidor.
          falhas.push(res.message || `A pauta "${item.titulo}" foi recusada pelo servidor.`);
        }
      } catch (err) {
        falhas.push(detalheLegivel(err, `Falha ao marcar a pauta "${item.titulo}".`));
      }
    }

    fetchBlueprint(selectedDate);

    if (falhas.length === 0) {
      const quantas = ok === 1 ? '1 pauta marcada' : `${ok} pautas marcadas`;
      showToast(
        `${quantas}. ${mensagemServidor || 'Vale apenas neste painel — nada foi despachado para esteira automática.'}`
      );
      return;
    }
    showToast(`${ok} ok, ${falhas.length} falharam: ${falhas[0]}`);
  };

  const showToast = (msg: string) => {
  // F-411 (familia): o codemod de i18n tinha colado um useTranslation()
  // DENTRO desta funcao — hook fora do ciclo de render, "Invalid hook
  // call" a cada chamada. Era por isso que toasts/acoes daqui morriam em
  // silencio. O `t` nem era usado.
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };

  const handleDateClick = (day: number) => {
    const dayStr = day < 10 ? `0${day}` : `${day}`;
    const monthStr = selectedMonth < 10 ? `0${selectedMonth}` : `${selectedMonth}`;
    const newDate = `${selectedYear}-${monthStr}-${dayStr}`;
    setSelectedDate(newDate);
  };

  // O mês tem os dias que tem. Estava fixo em 31: fevereiro renderizava 31 dias,
  // e abril, junho, setembro e novembro também. O dia 0 do mês seguinte é o
  // último do mês atual — jeito curto de acertar inclusive ano bissexto.
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  // Casas vazias antes do dia 1, para a coluna bater com o dia da semana. Sem
  // isto o cabeçalho dizia Dom-Sáb e o dia 1 caía SEMPRE em "Dom": o alinhamento
  // era decorativo, e calendário que mente sobre o dia da semana é pior que
  // calendário sem cabeçalho.
  const casasVazias = new Date(selectedYear, selectedMonth - 1, 1).getDay();

  // Filtragem de Itens
  const filterItems = (itens: BriefingItem[] = []) => {
    return itens.filter(item => {
      if (activeCategoryFilter !== 'TODAS' && !item.categoria.toUpperCase().includes(activeCategoryFilter)) {
        return false;
      }
      return true;
    });
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1600px', margin: '0 auto', color: 'var(--text-1)' }}>
      
      {/* Toast Notification */}
      {toastMsg && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--sig-ok, #10B981)',
          color: 'var(--sig-ok-text, #10B981)',
          padding: '12px 20px',
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '13px',
          boxShadow: 'var(--shadow-3, 0 10px 15px -3px rgba(0,0,0,0.1))',
          zIndex: 1300
        }}>
          {toastMsg}
        </div>
      )}

      {/* HEADER SUPERIOR DO COCKPIT */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--border-1)', paddingBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
              Blueprint do dia
            </h1>
            {/* Havia aqui um selo "[REUTERS PROTOCOL]". A Reuters e uma
                agencia de noticias real e nao tem relacao com este produto —
                usar a marca dela como enfeite de cabecalho sugere um vinculo
                que nao existe, para o cliente e para quem ele mostrar a tela. */}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-3)', margin: '4px 0 0 0', lineHeight: 1.5 }}>
            Ocasiões calculadas por regra (feriados, computus, datas presas a dia
            da semana) cruzadas com a antecedência MEDIDA de cada uma em oito anos
            de acesso diário real. O que a sua automação já publicou hoje aparece
            como feito — o resto é o que ainda não foi coberto.
          </p>
        </div>

        {/* Botões de Acesso e Ação em Lote */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handleDispatchAll}
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--nexus-brand, #3B82F6)',
              color: 'var(--text-on-accent, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <LightningIcon size={14} />
            Despachar Todas Sugeridas (1-Click)
          </button>

          <a
            href="/inteligencia/kanban-estrategico"
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-1)',
              border: '1px solid var(--border-2)',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 600
            }}
          >
            Kanban Estratégico
          </a>
        </div>
      </div>

      {/* HERO CARD: ORDEM DO DIA & PROGRESSO DA REDAÇÃO */}
      <div style={{
        backgroundColor: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: '8px',
        padding: '16px 20px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Ordem de Produção Diária
          </div>
          <h2 style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-1)', margin: '4px 0 2px 0' }}>
            {blueprint?.data_formatada || 'Carregando...'}
          </h2>
          <div style={{ fontSize: '12px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Semana {blueprint?.semana_ano}</span>
            <span>•</span>
            <span>Escopo Nacional (GEO=BR)</span>
            <span>•</span>
            {blueprint?.proveniencia?.janela_atencao && (
              <span style={{ color: 'var(--text-3)' }} title={blueprint.proveniencia!.fonte_atencao}>
                Antecedência medida em {blueprint.proveniencia!.janela_atencao}
              </span>
            )}
          </div>
        </div>

        {/* Barra de Progresso */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '180px' }}>
          <div style={{ fontSize: '16px', fontWeight: 800, color: blueprint && blueprint.progresso_pct === 100 ? 'var(--sig-ok, #10B981)' : 'var(--nexus-brand, #3B82F6)' }}>
            {blueprint?.pautas_concluidas || 0} / {blueprint?.meta_total_pautas || 0}
          </div>
          {/* "Despachadas" sugeria entrega a uma esteira. O contador conta o
              que foi marcado NESTA tela — e so isso e que ele sabe contar. */}
          <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600, marginBottom: '6px' }}>
            Pautas marcadas neste painel ({blueprint?.progresso_pct || 0}%)
          </div>
          <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--bg-2)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{
              width: `${blueprint?.progresso_pct || 0}%`,
              height: '100%',
              backgroundColor: blueprint && blueprint.progresso_pct === 100 ? 'var(--sig-ok, #10B981)' : 'var(--nexus-brand, #3B82F6)',
              borderRadius: '3px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {/* ROTULO HONESTO DO QUE ESTA ORDEM DO DIA E.
          O ciclo D-0 desta tela nao GERA pauta: GET /strategist/daily-blueprint
          (strategist.py:33-42) monta um dicionario e devolve — nada e gravado,
          nada e agendado, e nenhum laco de fundo do app (main.py:535-542) roda
          o Estrategista. O "Despachar" grava duas chaves num dict de modulo
          (daily_blueprint_service.py:479-493, com "persistido": False) cujo
          unico leitor e esta mesma tela. Enquanto a ponte para o Quadro nao
          existir, a tela diz isso em vez de deixar o operador procurar o card. */}
      <div style={{
        backgroundColor: 'var(--sig-warn-bg)',
        border: '1px solid var(--sig-warn-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '20px'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--sig-warn-text)', marginBottom: '4px' }}>
          Ainda não conectado — estas pautas são sugestões deste painel
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-2)', margin: 0, lineHeight: 1.5 }}>
          O ciclo D-0 lista o que vale a pena produzir hoje; ele não gera pauta
          em lugar nenhum. Nada nasce no Quadro de Produção automaticamente:
          &quot;Despachar&quot; marca o item aqui, nesta tela, e não aciona
          esteira nenhuma. Para virar matéria, a pauta ainda precisa ser aberta e
          escrita por alguém em Produção ou em Artigos (em construção).
        </p>
      </div>

      {/* FITA DE FILTROS RÁPIDOS POR VERTICAL */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginRight: '4px' }}>
          Filtrar Vertical:
        </span>
        {['TODAS', 'ECONOMIA', 'ESPORTES', 'LOTERIAS', 'SAZONALIDADE'].map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategoryFilter(cat)}
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: 'monospace',
              border: `1px solid ${activeCategoryFilter === cat ? 'var(--nexus-brand, #3B82F6)' : 'var(--border-1)'}`,
              backgroundColor: activeCategoryFilter === cat ? 'rgba(59, 130, 246, 0.12)' : 'var(--bg-1)',
              color: activeCategoryFilter === cat ? 'var(--nexus-brand, #3B82F6)' : 'var(--text-2)',
              cursor: 'pointer'
            }}
          >
            [{cat}]
          </button>
        ))}
      </div>

      {/* GRID PRINCIPAL: 65% FEED DE BRIEFS / 35% MINI-PLANNER LATERAL */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.95fr) minmax(340px, 1fr)', gap: '24px', alignItems: 'flex-start' }}>
        
        {/* COLUNA PRINCIPAL: 3 RAIAS COGNITIVAS */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {erro ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
              <p style={{ color: 'var(--text-1)', fontWeight: 600, marginBottom: '6px' }}>
                Não foi possível carregar a ordem do dia.
              </p>
              <p style={{ fontSize: '13px' }}>{erro}</p>
            </div>
          ) : loading || !blueprint ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
              Carregando a pauta do dia...
            </div>
          ) : (
            <>
              {/* RAIA 0: JÁ COBERTO PELA AUTOMAÇÃO.
                  Vem PRIMEIRO de propósito. O editor abre a tela às 7h e a
                  primeira coisa que precisa saber é o que a madrugada já
                  entregou — senão manda produzir de novo a loteria e o
                  horóscopo que saíram sozinhos às 6h. */}
              {blueprint.horizontes.ja_coberto && blueprint.horizontes.ja_coberto.itens.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ borderLeft: '3px solid var(--sig-ok, #10B981)', paddingLeft: '10px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 800, color: 'var(--sig-ok, #10B981)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {blueprint.horizontes.ja_coberto.label}
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '2px 0 0 0' }}>
                      {blueprint.horizontes.ja_coberto.descricao}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {blueprint.horizontes.ja_coberto.itens.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '8px 12px', borderRadius: '8px',
                          backgroundColor: 'var(--bg-2)',
                          border: '1px solid var(--border-1)'
                        }}
                      >
                        <span style={{ color: 'var(--sig-ok, #10B981)', fontSize: '13px' }} aria-hidden></span>
                        <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>
                          {item.titulo}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                          {item.fonte_primaria}
                          {item.publicado_em ? ` · ${new Date(item.publicado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noopener noreferrer"
                             style={{ fontSize: '11px', color: 'var(--nexus-brand, #3B82F6)' }}>
                            ver
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RAIA 1: GIRO DE HOJE (D-0 & D-1) */}
              {blueprint.horizontes.giro_hoje && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ borderLeft: '3px solid var(--sig-crit, #EF4444)', paddingLeft: '10px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 800, color: 'var(--sig-crit, #EF4444)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {blueprint.horizontes.giro_hoje.label}
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '2px 0 0 0' }}>
                      {blueprint.horizontes.giro_hoje.descricao}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filterItems(blueprint.horizontes.giro_hoje.itens).map((item) => (
                      <ExecutiveBriefingCard
                        key={item.id}
                        item={item}
                        onToggle={handleToggleItem}
                        onDispatch={handleDispatchItem}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* RAIA 2: FÁBRICA DE SEO (D-60 a D-15) */}
              {blueprint.horizontes.fabrica_seo && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ borderLeft: '3px solid var(--nexus-brand, #3B82F6)', paddingLeft: '10px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 800, color: 'var(--nexus-brand, #3B82F6)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {blueprint.horizontes.fabrica_seo.label}
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '2px 0 0 0' }}>
                      {blueprint.horizontes.fabrica_seo.descricao}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filterItems(blueprint.horizontes.fabrica_seo.itens).map((item) => (
                      <ExecutiveBriefingCard
                        key={item.id}
                        item={item}
                        onToggle={handleToggleItem}
                        onDispatch={handleDispatchItem}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* RAIA 3: RESCALDO (D+1) */}
              {blueprint.horizontes.rescaldo && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ borderLeft: '3px solid var(--sig-ok, #10B981)', paddingLeft: '10px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 800, color: 'var(--sig-ok, #10B981)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {blueprint.horizontes.rescaldo.label}
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '2px 0 0 0' }}>
                      {blueprint.horizontes.rescaldo.descricao}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filterItems(blueprint.horizontes.rescaldo.itens).map((item) => (
                      <ExecutiveBriefingCard
                        key={item.id}
                        item={item}
                        onToggle={handleToggleItem}
                        onDispatch={handleDispatchItem}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* COLUNA LATERAL: MINI-PLANNER FIXO (35%) */}
        <div style={{
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--border-1)',
          borderRadius: '8px',
          padding: '20px',
          position: 'sticky',
          top: '20px'
        }}>
          
          {/* Cabeçalho do Calendário */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: 'var(--text-1)' }}>
              Calendário Planner
            </h3>

            <div style={{ display: 'flex', gap: '6px' }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                style={{
                  backgroundColor: 'var(--bg-2)',
                  border: '1px solid var(--border-2)',
                  color: 'var(--text-1)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600
                }}
              >
                <option value="1">Janeiro</option>
                <option value="2">Fevereiro</option>
                <option value="3">Março</option>
                <option value="4">Abril</option>
                <option value="5">Maio</option>
                <option value="6">Junho</option>
                <option value="7">Julho</option>
                <option value="8">Agosto</option>
                <option value="9">Setembro</option>
                <option value="10">Outubro</option>
                <option value="11">Novembro</option>
                <option value="12">Dezembro</option>
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                style={{
                  backgroundColor: 'var(--bg-2)',
                  border: '1px solid var(--border-2)',
                  color: 'var(--text-1)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600
                }}
              >
                {/* Janela movel: o seletor fixo em 2026/2027 envelheceria
                    sozinho e, passado 2027, a tela nao teria como mostrar o
                    proximo ano — que e justamente o horizonte de planejamento. */}
                {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 1 + i).map((ano) => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '12px' }}>
            Selecione uma data para auditar a ordem do dia:
          </div>

          {/* Grid do Calendário (Dias 1 a 31) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '16px'
          }}>
            {/* `key={i}` colidia com os dias do mes: cabecalho e dias vivem na
                MESMA grade, entao as chaves 1..6 apareciam duas vezes e o React
                avisava "two children with the same key". Prefixo resolve. */}
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => (
              <div key={`cab-${d}`} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'var(--text-3)', paddingBottom: '2px' }}>
                {d}
              </div>
            ))}

            {Array.from({ length: casasVazias }).map((_, i) => (
              <div key={`vazio-${i}`} aria-hidden />
            ))}

            {daysArray.map((day) => {
              const dayStr = day < 10 ? `0${day}` : `${day}`;
              const monthStr = selectedMonth < 10 ? `0${selectedMonth}` : `${selectedMonth}`;
              const curDateStr = `${selectedYear}-${monthStr}-${dayStr}`;
              const isSelected = selectedDate === curDateStr;
              const events = calendarOverview?.dias_eventos?.[day] || [];

              return (
                <button
                  key={day}
                  onClick={() => handleDateClick(day)}
                  style={{
                    backgroundColor: isSelected ? 'var(--nexus-brand, #3B82F6)' : 'var(--bg-2)',
                    border: `1px solid ${isSelected ? 'var(--nexus-brand, #3B82F6)' : 'var(--border-1)'}`,
                    borderRadius: '4px',
                    padding: '6px 2px',
                    color: isSelected ? 'var(--text-on-accent, #ffffff)' : 'var(--text-1)',
                    fontSize: '12px',
                    fontWeight: isSelected ? 800 : 500,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '36px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>{day}</span>
                  {events.length > 0 && (
                    <div style={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: isSelected ? 'var(--text-on-accent, #ffffff)' : (events[0].tipo === 'd60' ? 'var(--chart-serie-5, #8B5CF6)' : (events[0].tipo === 'liquidez' ? 'var(--sig-ok, #10B981)' : 'var(--nexus-brand, #3B82F6)')),
                      marginTop: '2px'
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legenda de Gatilhos */}
          <div style={{
            backgroundColor: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: '6px',
            padding: '12px',
            fontSize: '11px',
            color: 'var(--text-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '16px'
          }}>
            <div style={{ fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Como ler as raias
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--chart-serie-5, #8B5CF6)' }} />
              <span><strong>D-60:</strong> Plantio de Indexação SEO</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--nexus-brand, #3B82F6)' }} />
              <span><strong>D-4 / D-0:</strong> Tabelas Oficiais e Factual</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--sig-ok, #10B981)' }} />
              <span><strong>Liquidez:</strong> Liberação de Renda Oficial</span>
            </div>
          </div>

          {/* Resumo de Liquidez do Mês Selecionado */}
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.10)',
            border: '1px solid var(--sig-ok, #10B981)',
            borderRadius: '6px',
            padding: '12px'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--sig-ok, #10B981)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Liquidez Mapeada: {calendarOverview?.mes_nome?.toUpperCase() || ''}
            </div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-1)', margin: '2px 0' }}>
              R$ {calendarOverview?.liquidez_mes_bi_rs?.toFixed(1) || '0.0'} Bilhões
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-2)', lineHeight: 1.4 }}>
              {calendarOverview?.destaque_mes || ''}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
